import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Kept as its own persisted store (not a slice of useAppStore) so session/user state never
// risks getting swept into localStorage persistence by a future refactor mistake — auth state
// and a booking draft have very different lifetimes and neither belongs coupled to the other's
// persistence behavior.
//
// Deliberately loosely typed (`Record<string, unknown>`, not NewRequestPage's FormValues) — this
// store doesn't need to know the wizard's exact field set, just persist whatever partial object
// it's handed. The page component owns the concrete shape.
type DraftValue = Record<string, unknown>;

interface RequestDraftStore {
  draft: DraftValue;
  step: number;
  savedAt: number | null;
  setDraft: (patch: DraftValue) => void;
  setStep: (step: number) => void;
  clear: () => void;
}

// Drafts older than this are presumed abandoned and discarded on load rather than resurrected.
export const DRAFT_MAX_AGE_MS = 48 * 60 * 60 * 1000;

export const useRequestDraftStore = create<RequestDraftStore>()(
  persist(
    (set) => ({
      draft: {},
      step: 0,
      savedAt: null,
      setDraft: (patch) => set((s) => ({ draft: { ...s.draft, ...patch }, savedAt: Date.now() })),
      setStep: (step) => set({ step, savedAt: Date.now() }),
      clear: () => set({ draft: {}, step: 0, savedAt: null }),
    }),
    {
      name: 'rt-new-request-draft-v1',
      partialize: (s) => ({ draft: s.draft, step: s.step, savedAt: s.savedAt }),
    },
  ),
);
